<script>
import JSConfetti from 'js-confetti';
import DOMPurify from 'dompurify';
import ContentBlockView from '@shared/mixins/ContentBlockView';

export default {
  name: 'ContentBlockScratchCardView',
  mixins: [ContentBlockView],
  data() {
    return {
      isDrawing: false,
      ctx: null,
      isCanvasReady: false,
      isCompleted: false,
      resizeObserver: null,
      debounceTimer: null,
      isInteractionStarted: false,
    };
  },

  mounted() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.handleResize({ width, height });
      }
    });

    this.resizeObserver.observe(this.$el);
  },
  computed: {
    content() {
      return DOMPurify.sanitize(this.getFormattedText(this.data.content));
    },
  },
  methods: {
    handleResize({ width, height }) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        const canvas = this.$refs.scratchCanvas;
        if (height > 0 && canvas && (canvas.width !== width || canvas.height !== height)) {
          this.setupCanvas({ width, height });
        }
      }, 50);
    },

    setupCanvas({ width, height }) {
      const canvas = this.$refs.scratchCanvas;
      if (!canvas) return;

      canvas.width = width;
      canvas.height = height;
      this.ctx = canvas.getContext('2d');

      if (!this.data || !this.data.scratchImage) {
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.isCanvasReady = true;
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvasRatio = canvas.width / canvas.height;
        const imageRatio = img.naturalWidth / img.naturalHeight;
        let newWidth;
        let newHeight;
        let x;
        let y;

        if (canvasRatio > imageRatio) {
          newHeight = canvas.height;
          newWidth = img.naturalWidth * (newHeight / img.naturalHeight);
          x = (canvas.width - newWidth) / 2;
          y = 0;
        } else {
          newWidth = canvas.width;
          newHeight = img.naturalHeight * (newWidth / img.naturalWidth);
          x = 0;
          y = (canvas.height - newHeight) / 2;
        }

        this.ctx.fillStyle = 'black';
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);

        this.ctx.drawImage(img, x, y, newWidth, newHeight);

        this.removeEventListeners();
        this.addEventListeners();
        this.isCanvasReady = true;
      };
      img.onerror = () => {
        console.error('Failed to load scratch image.');
        this.isCanvasReady = true;
      };
      img.src = this.data.scratchImage;
    },

    addEventListeners() {
      const canvas = this.$refs.scratchCanvas;
      if (!canvas) return;
      canvas.addEventListener('mousedown', this.startScratching);
      canvas.addEventListener('mousemove', this.scratch);
      canvas.addEventListener('mouseup', this.stopScratching);
      canvas.addEventListener('mouseleave', this.stopScratching);

      canvas.addEventListener('touchstart', this.startScratching, { passive: false });
      canvas.addEventListener('touchmove', this.scratch, { passive: false });
      canvas.addEventListener('touchend', this.stopScratching);
    },

    removeEventListeners() {
      const canvas = this.$refs.scratchCanvas;
      if (!canvas) return;
      canvas.removeEventListener('mousedown', this.startScratching);
      canvas.removeEventListener('mousemove', this.scratch);
      canvas.removeEventListener('mouseup', this.stopScratching);
      canvas.removeEventListener('mouseleave', this.stopScratching);

      canvas.removeEventListener('touchstart', this.startScratching);
      canvas.removeEventListener('touchmove', this.scratch);
      canvas.removeEventListener('touchend', this.stopScratching);
    },

    getPointerPosition(event) {
      const canvas = this.$refs.scratchCanvas;
      const rect = canvas.getBoundingClientRect();
      const clientX = event.touches ? event.touches[0].clientX : event.clientX;
      const clientY = event.touches ? event.touches[0].clientY : event.clientY;

      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    },

    startScratching(event) {
      if (!this.isInteractionStarted) return;
      this.isDrawing = true;
      this.scratch(event);
    },

    scratch(event) {
      if (!this.isDrawing || !this.ctx || !this.isInteractionStarted) return;

      if (event.touches) {
        event.preventDefault();
      }

      const pos = this.getPointerPosition(event);
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.beginPath();
      this.ctx.arc(pos.x, pos.y, 20, 0, 2 * Math.PI);
      this.ctx.fill();
    },

    stopScratching() {
      this.isDrawing = false;
      if (!this.isCompleted) {
        requestAnimationFrame(() => {
          this.checkCompletion();
        });
      }
    },

    checkCompletion() {
      const canvas = this.$refs.scratchCanvas;
      if (!this.ctx || !canvas || canvas.width === 0 || canvas.height === 0) return;

      const imageData = this.ctx
        .getImageData(0, 0, canvas.width, canvas.height);
      const { data } = imageData;
      const totalPixels = data.length / 4;
      let transparentPixels = 0;

      for (let i = 3; i < data.length; i += 4) {
        if (data[i] === 0) {
          transparentPixels++;
        }
      }

      const scratchedPercentage = (transparentPixels / totalPixels) * 100;

      if (scratchedPercentage >= 40) {
        this.isCompleted = true;
        if (this.data.has_confetti === true) {
          this.showConfetti();
        }
        canvas.style.transition = 'opacity 0.5s ease-out';
        canvas.style.opacity = '0';

        setTimeout(() => {
          this.isCanvasReady = false;
          this.removeEventListeners();
        }, 500);
      }
    },

    showConfetti() {
      this.jsConfetti = this.jsConfetti || new JSConfetti();
      this.jsConfetti.addConfetti({
        emojis: ['⚡️', '💥', '✨', '💫'],
        emojiSize: 50,
        confettiNumber: 80,
      });
    },
  },
  i18n: {
    messages: {
      en: {
        scratch_card: {
          start_scratching: 'Click to start scratching',
        },
      },
      fr: {
        scratch_card: {
          start_scratching: 'Cliquez pour commencer à gratter',
        },
      },
    },
  },

  beforeDestroy() {
    this.removeEventListeners();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.jsConfetti && this.jsConfetti.destroyCanvas();
  },
};
</script>

<template>
  <div class="compsc">
    <div class="box mb-0" style="min-height: 200px;">
      <figure v-if="data.image" class="image mb-5">
        <img
          ref="backgroundImage"
          :src="data.image.url || data.image"
          alt="Revealed content"
        />
      </figure>
      <div
        v-if="content"
        ref="backgroundHtml"
        class="w-full content"
        v-html="content"
      />
    </div>
    <canvas
      v-show="isCanvasReady"
      ref="scratchCanvas"
      class="compsc_canvas w-full h-full"
    />
    <div
      v-if="!isInteractionStarted"
      class="compsc_overlay"
      :class="{
        'has-background-black70': isCanvasReady,
        'has-background-black': !isCanvasReady,
      }"
    >
      <b-button
        type="is-primary"
        class="is-custom"
        @click.native="isInteractionStarted = true"
      >
        <span v-t="'scratch_card.start_scratching'" />
      </b-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.compsc {
  position: relative;

  &_canvas,
  &_overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &_canvas {
    z-index: 1;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  &_overlay {
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
